import React from "react";
import styled from "styled-components";
import { colors } from "./colors";
import { typeStyle } from "./Text";

/**
 * @typedef {Object} LabelProps
 * @property {React.ReactNode} children
 * @property {string} [variant='neutral'] - A key of colors.
 * @property {string} [className]
 */

/**
 * Which stop of the variant's scale draws the edge and the text.
 *
 * The original rule was "both at [900]" — outline it in its own colour — which is right for a hue
 * and wrong for neutral, because the two scales do not mean the same thing at the same stop.
 * In every hue, [900] *is* the colour: success #00856A, danger #E24444, primary #0085BE. In the
 * neutral scale, [900] is the darkest ink on the page, #1C343E. So the one formula produced a
 * near-black outline around a grey pill: drab, and heavier than the row's own name.
 *
 * A hue therefore keeps its colour, and neutral gets stops that match what it is for — a soft edge
 * and body-weight ink. This replaces a subtle boolean that said the same thing worse: the two
 * looks are not a preference a caller should have to know about, they follow from the variant.
 */
const edgeStop = (variant) => (variant === "neutral" ? 300 : 900);
const inkStop = (variant) => (variant === "neutral" ? 700 : 900);

const LabelText = styled.label`
    display: inline-flex;
    align-items: center;
    /* caption — the smallest step, which is what a chip is. */
    ${typeStyle("caption")}
    font-weight: 500;
    color: ${({ $variant }) => colors[$variant]?.[inkStop($variant)]};
    background-color: ${({ $variant }) => colors[$variant]?.[100]};
    border: 1px solid ${({ $variant }) => colors[$variant]?.[edgeStop($variant)]};
    padding: 4px 8px;
    border-radius: 6px;
    margin: 0;
    /* A label names one thing; breaking it across lines reads as two. */
    white-space: nowrap;
`;

/**
 * A short, bounded value: a status, a category, one of a known set.
 *
 * A hued label is for something a reader has to notice — the extension's "3 Deals" appears only
 * when there are won deals, once, in green. A neutral one is for a value that repeats down a
 * column, where the same treatment would turn the column into a stack of boxes and pull the eye
 * off the row's identity.
 *
 * @type {React.FC<LabelProps>}
 */
const Label = ({ children, variant = "neutral", className }) => {
    return (
        <LabelText className={className} $variant={variant}>
            {children}
        </LabelText>
    );
};

export default Label;
